import React, {
  useEffect,
  useContext,
  useMemo,
  useRef,
  useState,
  forwardRef,
} from "react";
import {
  CalendarDays,
  MapPin,
  User,
  Globe,
  Crown,
  Share2,
  Check,
  ChevronDown,
  Search,
  ExternalLink,
  Star,
  Facebook,
  Twitter,
  Linkedin,
  Mail,
  LanguagesIcon,
  ChevronRight,
  Link as LinkIcon,
  Video,
  Instagram,
  Music,
  MessageCircle,
  Send,
  Camera,
} from "lucide-react";
import Header3 from "../components/Header3";
import { GoogleMap, LoadScript, Marker } from "@react-google-maps/api";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "../AxiosInstance";
import { ChatState } from "../context/ChatProvider";
import SocketContext from "../context/SocketContext";
import toast from "react-hot-toast";
import { BASE_URL } from "../config/BaseUrls";
import { useAuth } from "../context/auth";
import { Frontend } from "../config/BaseUrls";
import { useSearchParams } from "react-router-dom";
import { LanguageContext } from "../context/LanguageContext";
import { Helmet } from "react-helmet-async";
  import { resolveBookImageUrl } from "../utils/urlHelpers";

// ---------- Layout primitives ----------
const Container = ({ children }) => (
  <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
    {children}
  </div>
);

// Allow refs on Card so click-outside works correctly
const Card = forwardRef(function Card({ children, className = "" }, ref) {
  return (
    <div
      ref={ref}
      className={`rounded-3xl bg-white p-4 shadow-sm ring-1 ring-[#f2f2f2] ${className}`}
    >
      {children}
    </div>
  );
});

const Pill = ({ children, className = "" }) => (
  <span
    className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${className}`}
  >
    {children}
  </span>
);

const Button = ({
  variant = "primary",
  className = "",
  children,
  disabled = false,
  ...props
}) => {
  const base =
    "inline-flex items-center justify-center gap-[8px] rounded-2xl px-[20px] py-[8px] text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed";
  const styles = {
    primary: "bg-[#34c3c9] text-white hover:brightness-95 focus:ring-[#34c3c9]",
    secondary:
      "bg-white text-[#34c3c9] ring-1 ring-[#34c3c9] hover:bg-[#f2f2f2] focus:ring-[#34c3c9]",
    ghost: "text-[#34c3c9] hover:bg-[#f2f2f2] focus:ring-[#34c3c9]",
  };
  return (
    <button
      className={`${base} ${styles[variant]} ${className}`}
      disabled={disabled}
      {...props}
    >
      {children}
    </button>
  );
};

const Progress = ({ value }) => (
  <div className="h-2 w-full rounded-full bg-[#f2f2f2]">
    <div
      className="h-2 rounded-full bg-[#34c3c9]"
      style={{ width: `${Math.min(Math.max(value, 0), 100)}%` }}
    />
  </div>
);

const EventMap = ({ locationName }) => {
  const [coordinates, setCoordinates] = useState(null);

  const mapStyles = {
    height: "100%",
    width: "100%",
    borderRadius: "16px",
  };

  const handleMapLoad = () => {
    if (!locationName || !window.google) return;

    const geocoder = new window.google.maps.Geocoder();

    geocoder.geocode({ address: locationName }, (results, status) => {
      if (status === "OK" && results[0]) {
        const { lat, lng } = results[0].geometry.location;
        setCoordinates({
          lat: lat(),
          lng: lng(),
        });
      } else {
        console.error("Geocoding failed:", status);
      }
    });
  };

  return (
    <LoadScript
      onLoad={handleMapLoad}
      googleMapsApiKey="AIzaSyDopg1y0GhLx_WTxXyoT-0MkOVQ34CmpDI"
    >
      {coordinates && (
        <GoogleMap mapContainerStyle={mapStyles} center={coordinates} zoom={15}>
          <Marker position={coordinates} />
        </GoogleMap>
      )}
    </LoadScript>
  );
};

const GroupEvent = () => {
  // Get user's timezone from browser settings (not IP-based)
  // This uses the browser's timezone setting, so VPN won't change it unless browser timezone changes
  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const { lg } = useContext(LanguageContext);

  const [searchParams] = useSearchParams();
  const socket = useContext(SocketContext);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef(null);
  const { user, setShowLoginM } = ChatState();

  const [isJoined, setIsJoined] = useState(false);

  const handleShowW = async () => {
    if (!user?._id) {
      setShowLoginM(true);
      return;
    }
    if (!user?._id || !eventData?.userId?._id) {
      toast.error("Missing required user or event information");
      return;
    }
    if (user?._id == eventData?.userId?._id) {
      toast.error("Event creator could not join event as attendee.");
      return;
    }
    if (socket) {
      console.log(eventData, "eventData");
      socket.emit("request_chat", {
        userId: user?._id,
        ambassadorId: eventData?.userId?._id,
        eventData: { ...eventData, location: eventData?.location },
      });
      toast.success(lg?.yourRequestHasBeenSent || "Your request has been sent to the ambassador");
    } else {
      toast.error("You must be logged in to request a chat.");
    }
    if (spotsLeft > 0) {
      try {
        // 1. Call API to update attendees
        const response = await axios.put(
          `api/v1/books/update-attendees/${eventData?.eventId}`,
          {
            attendeeIds: [...eventData?.attendees, user?._id], // Assuming you have currentUserId
          }
        );

        // 2. Only update state if API call succeeds
        if (response.status === 200) {
          const isAlreadyAttending = eventData?.attendees?.some(
            (attendeeId) => attendeeId.toString() === user?._id.toString()
          );

          if (!isAlreadyAttending) {
            // 2. Update spots left (only if there are spots available)
            const updatedSpotsLeft = Math.max(0, spotsLeft - 1); // Prevent negative values
            // 3. Update state and localStorage
            setIsJoined(true);
            //setWShow(true); // Open signup modal
            setSpotsLeft(updatedSpotsLeft);
            setShowJoined(true);
            const updatedEventData = {
              ...eventData,
              attendees: [...(eventData?.attendees || []), user?._id], // Safe array fallback
            };

            localStorage.setItem(
              "eventDataNew",
              JSON.stringify(updatedEventData)
            );
          } else {
            console.warn("User is already attending this event");
            // Optional: Show toast/alert to user
          }
        }
      } catch (error) {
        console.error("Failed to join event:", error);
        // Optional: Show error to user
        toast.error(
          error.response?.data?.message || "Join failed. Please try later."
        );
      }
    }
    // setIsJoined(true); // Assuming you have a state variable 'isJoined' to track if the user has joined
  };

  // ?FET DATA DANMACALLY?
  const [eventData, setEventData] = useState(null);
  const eventId = searchParams.get("bookid") || searchParams.get("id");
  useEffect(() => {
    const getEventData = async () => {
      console.log(eventId, "eventId eventId eventId eventId eventId");

      if (eventId) {
        // If eventId exists, fetch from backend
        try {
          const res = await axios.get(`api/v1/books/${eventId}`, {
            headers: { "x-timezone": userTimeZone },
          });
          const raw = res.data;
          if (raw?.maxAttendee === "no-limit") {
            setSpotsLeft(lg?.noLimit || "No Limit");
            setTotalSpots(lg?.noLimit || "No Limit");
          } else {
            setTotalSpots({ max: raw?.maxAttendee, min: raw?.minAttendee });
            const spotsLeft =
              Number(raw?.maxAttendee) - (raw?.attendees?.length || 0);
            setSpotsLeft(spotsLeft);
          }
          setEventData(formatEventData(raw, eventId));
        } catch (error) {
          console.error("Error fetching event data", error);
          // If fetch fails, try localStorage as fallback
          checkLocalStorage();
        }
      } else {
        // If no eventId, check localStorage
        checkLocalStorage();
      }
    };
    const formatEventData = (raw, id) => {
      return {
        type: raw.name,
        location: raw?.location?.name || "",
        city: raw.city || "",
        link: raw.link || "",
        title: raw.title || "",
        pages: raw.pageNumber || "",
        image: raw?.image || "",
        author: raw.author || "",
        age: raw.age || "",
        language: raw.language || "",
        utcTime: raw.globalTime || "",
        gender: raw.gender || "Unspecified",
        detail: raw.description || "",
        eventType: raw.type || "",
        publisher: raw.publisher || "",
        createdAt: raw?.dateInfo,
        time: raw?.timeTrack,
        localTime: raw?.localTime || null, // ✅ Backend-converted time to viewer's timezone
        userId: raw.userId || {},
        meetingLink: raw.meetingLink || "",
        eventId: raw._id || id,
        attendees: raw.attendees || [],
        LangLat: raw?.location || null,
        EAN: raw.EAN,
        aboutBook: raw.aboutBook || "",
        rating: raw.rating,
        totalReviews: raw.totalReviews,
      };
    };
    const checkLocalStorage = () => {
      const storedData = localStorage.getItem("eventDataNew");
      console.log(storedData, "storedData storedData storedData");
      if (storedData) {
        const parsedData = JSON.parse(storedData);
        console.log(parsedData, "parsedData parsedData parsedData");
        setEventData(parsedData);
      }
    };

    getEventData();
  }, [eventId]);
  const [isExpanded, setIsExpanded] = useState(false);

  // Full text content
  const limit = 70;
  const fullText = eventData?.aboutBook || eventData?.title || "";
  // Shortened text to show initially
  const isLongText = fullText.length > limit;
  const shortText = fullText.slice(0, limit); // Adjust the length as needed
  // const shortText = fullText.slice(0, limit);
  const navigate = useNavigate();
  const [isOpenWell, setIsOpenWell] = useState(false);

  const handleOpen = () => setIsOpenWell(true);
  const handleClose = () => setIsOpenWell(false);
  useEffect(() => {
    // Scroll to the top of the page when the component mounts
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const [totalSpots, setTotalSpots] = useState();
  const [spotsLeft, setSpotsLeft] = useState();

  useEffect(() => {
    localStorage.setItem("spotsLeftlast", spotsLeft);
  }, [spotsLeft]);

  // "Evnet craete"

  useEffect(() => {
    const storedData = localStorage.getItem("eventDataNew");
    if (!storedData) return;

    const parsedData = JSON.parse(storedData);

    if (parsedData?.attendees?.includes(user?._id)) {
      setIsJoined(true);
    }
  }, [user?._id]);

  const [countries, setCountries] = useState([]);
  const [buyCatalog, setBuyCatalog] = useState({}); // { "United Kingdom": [...], ... }
  const [buyMenuOpen, setBuyMenuOpen] = useState(false);
  const [activeCountry, setActiveCountry] = useState(null);
  const [buyQuery, setBuyQuery] = useState("");
  const buyMenuRef = useRef(null);
  const hasManuallySelectedCountry = useRef(false);
  const allCountries = Object.keys(buyCatalog);

  // ✅ Detect user's country automatically (only once, and only if user hasn't manually selected)
  useEffect(() => {
    // Don't auto-detect if user has manually selected a country
    if (hasManuallySelectedCountry.current) {
      return;
    }

    // Only auto-detect if buyCatalog is loaded and activeCountry is not set
    if (Object.keys(buyCatalog).length === 0 || activeCountry !== null) {
      return;
    }

    const detectCountry = async () => {
      try {
        const res = await fetch("https://ipwho.is/");
        const data = await res.json();
        console.log(data, "ip dres");

        if (data && data.country) {
          const country = data.country;
          // Only auto-select if it's part of the buyCatalog and user hasn't manually selected
          if (Object.keys(buyCatalog).includes(country) && !hasManuallySelectedCountry.current) {
            setActiveCountry(country);
          }
        }
      } catch (err) {
        console.error("Failed to detect country:", err);
      }
    };

    detectCountry();
  }, [buyCatalog, activeCountry]);
  useEffect(() => {
    const fetchStores = async () => {
      try {
        const { data } = await axios.get("api/v1/stores/");

        // Transform into { country: [stores] } shape
        const catalog = {};
        data.forEach((entry) => {
          catalog[entry.country.trim()] = entry.stores.map((store) => ({
            name: store.name,
            url: store.link,
          }));
        });

        console.log("Transformed buyCatalog:", catalog);
        setBuyCatalog(catalog);

        const countryList = data.map((entry) => entry.country.trim());
        console.log("Countries list:", countryList);
        setCountries(countryList);
      } catch (error) {
        console.error("Error fetching stores:", error);
      }
    };

    fetchStores();
  }, []);

  // FAtch User Details
  const [auth, setAuth] = useAuth();

  const userId = auth?.user?._id;

  const [users, setUsers] = useState(null);

  useEffect(() => {
    const fetchUser = async () => {
      if (!userId) return; // Don't make API call if userId is undefined
      try {
        const res = await axios.get(`/api/v1/auth/user-profile/${userId}`);
        setUsers(res.data.data);
      } catch (err) {
        console.error("Error:", err);
      }
    };

    fetchUser();
  }, [userId]);

  const shortToFullMonth = {
    Jan: "January",
    Feb: "February",
    Mar: "March",
    Apr: "April",
    May: "May",
    Jun: "June",
    Jul: "July",
    Aug: "August",
    Sep: "September",
    Oct: "October",
    Nov: "November",
    Dec: "December",
  };

  // Example:
  const shortMonth = eventData?.createdAt?.month; // e.g., "Jan"
  const fullMonth = shortToFullMonth[shortMonth] || shortMonth; // fallback in case undefined

  // Format time to 24-hour format (HH:mm)
  function formatTime24(time24) {
    if (!time24) return "";
    
    // Handle both "HH:mm" string format and number format
    let hours, minutes;
    if (typeof time24 === "string" && time24.includes(":")) {
      [hours, minutes] = time24.split(":").map(Number);
    } else if (typeof time24 === "number") {
      hours = time24;
      minutes = 0;
    } else {
      return "";
    }

    // Ensure 24-hour format: HH:mm
    const hoursStr = hours.toString().padStart(2, "0");
    const minutesStr = minutes ? minutes.toString().padStart(2, "0") : "00";

    return `${hoursStr}:${minutesStr}`;
  }

  // Use localTime (converted by backend) if available, otherwise fallback to original time
  const displayTime = eventData?.localTime 
    ? formatTime24(eventData.localTime)
    : eventData?.time?.minutes !== undefined && eventData?.time?.minutes !== null
    ? (() => {
        // Convert number (hour) to HH:mm format
        const hour = eventData.time.minutes;
        const minutes = eventData.time.seconds || 0;
        return formatTime24(`${hour}:${minutes.toString().padStart(2, "0")}`);
      })()
    : "";

  const getLanguageName = (code) => {
    try {
      return new Intl.DisplayNames(["en"], { type: "language" }).of(code);
    } catch {
      return code || "Unknown";
    }
  };
  const [reviews, setReviews] = useState([]);
  const handleBookCoverClick = () => {
    console.log(eventData, "eventData eventData eventData");

    const selectedBookObj = {
      bookId: eventId, // 👈 Use book.id if available
      bookCoverImage: eventData?.image, // <== This is correct
      title: eventData.title,
      author: eventData.author,
      descriptions: eventData.aboutBook, // ✅ add this
      titless: eventData.title,
      publisher: eventData.publisher,
      book: eventData,
      EAN: eventData.EAN, // 👈 Add EAN if available
      isSearchBook: true, // 👈 Add this if coming from searchbook
    };

    localStorage.setItem(
      "selectedBookforreviews",
      JSON.stringify(selectedBookObj)
    );
    localStorage.setItem(
      "selectedBookforreviewsnew",
      JSON.stringify(selectedBookObj)
    );
    navigate(`/reviews?bookid=${eventId}&searchbook=true`);
  };

  const [loading, setLoading] = useState(true); // Track loading state
  const [averageRating, setAverageRating] = useState(0);
  const [totalReviews, setTotalReviews] = useState(0);

  // Define fetchReviews outside of useEffect so you can call it from other functions
  const fetchReviews = async (bookIdFromURL, offilioFlag, searchBookFlag) => {
    console.log(bookIdFromURL, "bookIdFromURLbookIdFromURL");

    if (!bookIdFromURL) return; // Wait until userId and bookId are available

    setLoading(true); // Start loading
    try {
      const response = await axios.get(
        `api/v1/review/getreviews/${bookIdFromURL}?offilio=${offilioFlag}&searchbook=${searchBookFlag}`
      );
      // setReviews(response.data.reviews);
      const reviewsData = response.data.reviews || [];
      setReviews(reviewsData);
      console.log("📦 Reviews from backend:", reviewsData);
      // setCoverImage(response.data.bookCoverImage);

      setAverageRating(response.data.averageRating);
      setTotalReviews(response.data.totalReviews);
      setLoading(false);
    } catch (error) {
      console.error(error);
      toast.error("Failed to fetch reviews or user names");
      setLoading(false);
    }
  };
  const location = useLocation();

  const searchParamss = new URLSearchParams(location?.search);
  const searchbook = searchParamss.get("searchbook");

  useEffect(() => {
    const searchParams = new URLSearchParams(location?.search);
    const bookIdFromURL = searchParams.get("bookid");
    const offilioFlag = searchParams.get("offilio");
    const searchBookFlag = searchParamss.get("searchbook");

    fetchReviews(bookIdFromURL, offilioFlag, searchBookFlag);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSelectedCountry(null);
        setSearchTerm("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  //Added new code here
  const TitleStyles = () => (
    <style>{`
      .book-title { font-size: clamp(1.25rem, 2vw + 0.5rem, 2rem); line-height: 1.2; word-break: break-word; overflow-wrap: anywhere; hyphens: auto; display: -webkit-box; -webkit-box-orient: vertical; overflow: hidden; }
      @media (max-width: 639px) { .book-title { -webkit-line-clamp: 2; } }
      @media (min-width: 640px) { .book-title { -webkit-line-clamp: 2; } }
      @media (min-width: 1024px) { .book-title { -webkit-line-clamp: 3; } }
    `}</style>
  );

  const [joined, setJoined] = useState(false);
  const [showJoined, setShowJoined] = useState(false);
  const [showShare, setShowShare] = useState(false);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setBuyMenuOpen(false);
    const onClick = (e) => {
      if (buyMenuRef.current && !buyMenuRef.current.contains(e.target))
        setBuyMenuOpen(false);
    };
    window.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onClick);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onClick);
    };
  }, []);

  const filteredCountries = countries.filter((country) =>
    country.toLowerCase().includes(buyQuery.toLowerCase())
  );

  // Stars with half support
  const renderStars = (rating) =>
    Array.from({ length: 5 }, (_, i) => {
      const full = i + 1 <= Math.floor(rating);
      const half = !full && i < rating;
      return (
        <div
          key={i}
          className="relative h-[17px] w-[17px]"
          data-testid={`star-${i}`}
        >
          <Star className="absolute inset-0 h-[17px] w-[17px] text-gray-300" />
          {full && (
            <Star className="absolute inset-0 h-[17px] w-[17px] text-yellow-400 fill-yellow-400" />
          )}
          {half && (
            <Star
              className="absolute inset-0 h-[17px] w-[17px] text-yellow-400"
              style={{ clipPath: "inset(0 50% 0 0)" }}
              fill="#fbbf24"
            />
          )}
        </div>
      );
    });

  // const spotsLeft = mock.totalSpots - mock.takenSpots;
  const pct = typeof spotsLeft === "number" && typeof totalSpots === "object" && totalSpots?.max
    ? Math.round(((totalSpots.max - spotsLeft) / totalSpots.max) * 100)
    : 0;
  //const shareUrl = `${Frontend}/meetorganizer?id=${eventId}`;

  // Social share
  const eventTitle =
    `Check out this event! ${eventData?.title}` || "Check out this event!";
  const eventImage = resolveBookImageUrl(eventData?.image);
  const shareImage = eventImage || `${Frontend}/logo512.png`;

  const baseApiUrl = `${BASE_URL}/share`;
  const shareUrl = `${baseApiUrl}/${eventData?.eventId}`; // this is the new backend route
  const encodedUrl = encodeURIComponent(shareUrl);
  const encodedTitle = encodeURIComponent(eventTitle || "Check out this event!");

  return (
    <>
      <Helmet>
        <title>{eventTitle} | Discover reading events & more</title>
        {/* Open Graph Meta Tags */}
        <meta property="og:title" content={eventTitle} />
        <meta property="og:description" content={""} />
        <meta property="og:image" content={shareImage} />
        <meta property="og:url" content={shareUrl} />
        <meta property="og:type" content="website" />
        {/* Twitter Card Meta Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={eventTitle} />
        <meta name="twitter:description" content={""} />
        <meta name="twitter:image" content={shareImage} />
      </Helmet>

      <div className="position-fixed z-[400] w-100 left-0  top-0 grouup">
        <Header3 />
      </div>

      <div className="min-h-screen bg-gradient-to-b from-white to-[#f2f2f2] pb-52 pt-[118px]">
        <TitleStyles />
        <Container>
          {/* Top grid */}
          <div className="grid grid-cols-1 gap-6 md:grid-cols-12">
            {/* Cover + Details */}
            <Card className="md:col-span-7 grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Book cover + stars */}
              <div className="md:col-span-1 flex flex-col items-center">
                <div className="relative aspect-[2/3] w-full max-w-[300px] overflow-hidden rounded-md shadow-lg ring-1 ring-[#d1d1d1]">
                  {eventImage && (
                    <img
                      src={shareImage}
                      style={{
                        opacity: spotsLeft === 0 ? 0.3 : 1, // Dim the image when spotsLeft is 0
                      }}
                      onClick={handleBookCoverClick}
                      alt="Book cover"
                      className="h-full w-full object-cover cursor-pointer"
                    />
                  )}
                  {spotsLeft === 0 && (
                    <div
                      className="fw-bold rounded-1 fontfull"
                      style={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%) rotate(-70deg)",
                        color: "red",
                        fontSize: "52px",
                        backgroundColor: "transparent",
                        padding: "2px 35px",
                        border: "5px solid red",
                        textTransform: "uppercase",
                      }}
                    >
                      Full
                    </div>
                  )}
                </div>
                <div className="mt-3 flex items-center gap-2">
                  <div className="flex">
                    {renderStars(Math.floor(Number(averageRating || eventData?.rating || 0)))}
                  </div>
                  <span className="text-sm text-slate-600">
                    ({totalReviews || eventData?.totalReviews || 0} reviews)
                  </span>
                </div>
              </div>

              {/* Right column */}
              {eventData && (
                <div className="md:col-span-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <Pill className="bg-[#d9ead3] text-slate-700">
                      BOOK CLUB
                    </Pill>
                  </div>
                  <h1
                    className="mt-2 book-title font-semibold tracking-tight"
                    title={eventData?.title}
                    data-testid="book-title"
                  >
                    {eventData?.title}
                  </h1>

                  <p className="text-sm text-[#a6a6a6] break-words">
                    by {eventData?.author}
                    {eventData?.pages
                      ? ` - ${parseInt(eventData.pages, 10)} pages`
                      : ""}
                  </p>

                  <div className="mt-4 space-y-2 text-sm">
                    <div className="flex items-start gap-2 text-slate-700">
                      <CalendarDays className="mt-0.5" size={18} />
                      <div>
                        <span className="font-medium"> {lg?.when}:</span>{" "}
                        {(() => {
                          // Translate day abbreviation
                          const dayMap = {
                            "Mon": lg?.dayMon || "Mon",
                            "Tue": lg?.dayTue || "Tue",
                            "Wed": lg?.dayWed || "Wed",
                            "Thu": lg?.dayThu || "Thu",
                            "Fri": lg?.dayFri || "Fri",
                            "Sat": lg?.daySat || "Sat",
                            "Sun": lg?.daySun || "Sun",
                          };
                          const translatedDay = dayMap[eventData?.createdAt?.dayName] || eventData?.createdAt?.dayName;
                          return translatedDay + ",  " + eventData?.createdAt?.day + " " + fullMonth + " " + eventData?.createdAt?.year;
                        })()}
                        {displayTime && (
                          <span className="ml-2 font-medium text-[#34c3c9]">
                            at {displayTime}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-start gap-2 text-slate-700">
                      {eventData?.eventType === "On-line" ? (
                        <Video className="mt-0.5" size={18} />
                      ) : (
                        <MapPin className="mt-0.5" size={18} />
                      )}
                      <div>
                        <span className="font-medium"> {lg?.where}:</span>{" "}
                        {eventData?.eventType === "On-line" ? (
                          isJoined ? (
                            <a
                              href={eventData?.meetingLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-[#34c3c9] hover:underline"
                              style={{
                                cursor: "pointer",
                                opacity: "1",
                                fontSize: "16px",
                              }}
                            >
                              {eventData?.meetingLink}
                            </a>
                          ) : (
                            <>
                              On-line
                              <span style={{ fontSize: "13px", color: "gray" }}>
                                –{" "}
                                {lg?.linkVisibleAttendees ||
                                  "Link visible to attendees only"}
                              </span>
                            </>
                          )
                        ) : (
                          <a
                            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                              `${eventData?.location}, ${eventData?.city}`
                            )}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[#34c3c9] hover:underline"
                          >
                            {eventData?.location && `${eventData?.location}, `}
                            {eventData?.city}
                          </a>
                        )}
                      </div>
                    </div>
                    <div className="flex items-start gap-2 text-slate-700">
                      <User className="mt-0.5" size={18} />
                      <div>
                        <span className="font-medium">
                          {eventData?.userId?.isAmbassador
                            ? (lg?.ambassadorLabel || "Ambassador:")
                            : (lg?.createdBy || "Created BY:")}
                        </span>{" "}
                        <span
                          className="text-[#34c3c9] hover:underline cursor-pointer"
                          onClick={() => {
                            if (!userId) {
                              setShowLoginM(true);
                            } else
                              navigate(
                                `/ambassadorprofile?id=${eventData?.userId?._id}`
                              );
                          }}
                        >
                          {eventData?.userId?.name}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-start gap-2 text-slate-700">
                      <Globe className="mt-0.5" size={18} />
                      <div>
                        <span className="font-medium"> {lg?.language}:</span>{" "}
                        {getLanguageName(eventData?.language)}
                      </div>
                    </div>
                    <div className="flex items-start gap-2 text-slate-700">
                      <Crown className="mt-0.5" size={18} />
                      <div>
                        <span className="font-medium"> {lg?.age}:</span>{" "}
                        {eventData?.age}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </Card>

            {/* Map & Buy (Buy on resides here, above the map) */}
            <Card className="md:col-span-5 " ref={buyMenuRef}>
              <label className="mb-3 block text-sm font-semibold text-slate-700">
                {lg?.buyon || "Buy on"}
              </label>

              {/* Country Selector Button */}
              <button
                type="button"
                onClick={() => setBuyMenuOpen((v) => !v)}
                className="flex w-full max-w-md items-center justify-between rounded-xl border border-[#e5e5e5] bg-white px-3 py-2 text-sm text-slate-800 hover:bg-[#fafafa] transition-colors focus:outline-none focus:ring-2 focus:ring-[#34c3c9] focus:ring-offset-2"
                aria-expanded={buyMenuOpen}
              >
                <span className="font-medium">{activeCountry || (lg?.chooseYourCountry || "Choose your country")}</span>
                <ChevronDown className={`h-4 w-4 transition-transform ${buyMenuOpen ? 'rotate-180' : ''}`} />
              </button>

              {buyMenuOpen && (
                <div className="relative z-20 mt-2 w-full max-w-2xl overflow-hidden rounded-2xl border border-[#e5e5e5] bg-white shadow-xl">
                  {/* Search */}
                  <div className="border-b border-[#f0f0f0] p-3">
                    <div className="relative">
                      <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-[#a6a6a6]" />
                      <input
                        value={buyQuery}
                        onChange={(e) => setBuyQuery(e.target.value)}
                        placeholder="Search country..."
                        className="w-full rounded-lg border border-[#e5e5e5] bg-white py-2 pl-9 pr-3 text-sm focus:border-[#34c3c9] focus:outline-none"
                      />
                    </div>
                  </div>

                  {/* Two-pane nested menu */}
                  <div className="grid grid-cols-1 gap-0 sm:grid-cols-2">
                    {/* Countries */}
                    <div className="max-h-80 overflow-auto">
                      {filteredCountries.length === 0 ? (
                        <div className="px-4 py-8 text-center text-sm text-slate-500">
                          No countries
                        </div>
                      ) : (
                        filteredCountries.map((c) => (
                          <button
                            key={c}
                            onClick={() => {
                              hasManuallySelectedCountry.current = true;
                              setActiveCountry(c);
                            }}
                            className={`flex w-full items-center justify-between px-4 py-2 text-left text-sm hover:bg-[#f9f9f9] transition-colors ${
                              activeCountry === c
                                ? "bg-[#f6fffe] text-[#0f766e] font-medium"
                                : ""
                            }`}
                          >
                            <span>{c}</span>
                            <ChevronDown className="h-4 w-4 -rotate-90" />
                          </button>
                        ))
                      )}
                    </div>

                    {/* Sellers */}
                    <div className="border-t border-[#f2f2f2] sm:border-l sm:border-t-0 max-h-80 overflow-auto">
                      {!activeCountry ? (
                        <div className="px-4 py-8 text-center text-sm text-slate-500">
                          <p className="mb-1">Select a country to see sellers</p>
                          <p className="text-xs text-slate-400">Choose from the list on the left</p>
                        </div>
                      ) : !buyCatalog[activeCountry] || buyCatalog[activeCountry].length === 0 ? (
                        <div className="px-4 py-8 text-center text-sm text-slate-500">
                          <p className="mb-1">No sellers available</p>
                          <p className="text-xs text-slate-400">for {activeCountry}</p>
                        </div>
                      ) : (
                        <div>
                          <div className="sticky top-0 bg-white border-b border-[#f2f2f2] px-4 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                            {activeCountry} ({buyCatalog[activeCountry]?.length || 0} {buyCatalog[activeCountry]?.length === 1 ? 'seller' : 'sellers'})
                          </div>
                          <div className="divide-y divide-[#f2f2f2]">
                            {buyCatalog[activeCountry]?.map((s, index) => (
                            <a
                                key={s.name || index}
                                href={s.url || s.link || '#'}
                              target="_blank"
                              rel="noreferrer"
                                className="flex items-center justify-between px-4 py-3 text-sm hover:bg-[#f6fffe] transition-colors group"
                              onClick={() => setBuyMenuOpen(false)}
                            >
                                <span className="font-medium text-slate-700 group-hover:text-[#34c3c9] transition-colors">{s.name}</span>
                                <ExternalLink className="h-4 w-4 text-[#a6a6a6] group-hover:text-[#34c3c9] transition-colors" />
                            </a>
                          ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
              {eventData?.eventType === "In-person" ? (
                <div className="mt-6 overflow-hidden rounded-2xl ring-1 ring-[#f2f2f2]">
                  <div className=" h-[240px] w-full ">
                    <a
                      href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                        `${eventData?.location}, ${eventData?.city}`
                      )}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        textDecoration: "none",
                        color: "inherit",
                        textDecorationLine: "underline",
                        fontSize: "20px",
                      }} // Preserve styles and text color
                    >
                      {" "}
                      {eventData?.eventType === "In-person" && (
                        <EventMap
                          locationName={`${eventData?.location}, ${eventData?.city}`}
                        />
                      )}
                    </a>
                  </div>
                </div>
              ) : (
                <>
                  <div className="mt-6 grid h-[200px] w-full place-items-center rounded-2xl bg-[conic-gradient(at_top_left,_#e8f9fa,_#ffffff)] text-sm text-[#a6a6a6]">
                    {isJoined ? (
                      <a
                        href={eventData?.meetingLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[#34c3c9] hover:underline"
                        style={{
                          cursor: "pointer",
                          opacity: "1",
                          fontSize: "16px",
                        }}
                      >
                        <Video size={28} className="text-[#34c3c9]" />
                      </a>
                    ) : (
                      <Video size={28} className="text-[#34c3c9]" />
                    )}
                  </div>
                  <p className="mt-2 text-center text-xs text-slate-500">
                    {isJoined
                      ? "Click on the camera to join the meeting"
                      : ` ${
                          lg?.linkVisibleAttendees ||
                          "Link visible to attendees only"
                        }`}
                  </p>
                </>
              )}
            </Card>
          </div>
          {/* About */}
          <div className="mt-10">
            <Card className="md:col-span-12">
              <h2 className="text-lg font-semibold">{lg?.aboutbook}</h2>
              <div className="mt-3 space-y-3 text-sm leading-6 text-slate-700">
                <p style={{ whiteSpace: "pre-wrap" }}>
                  {" "}
                  {isExpanded || !isLongText ? fullText : `${shortText}...`}
                  {isLongText && (
                    <span
                      className="fw-bold"
                      style={{ cursor: "pointer" }}
                      onClick={() => setIsExpanded(!isExpanded)}
                    >
                      {isExpanded ? lg?.readless : lg?.readmore}
                    </span>
                  )}
                  {/* A powerful historical novel exploring the rise of fascism in
                  Italy and the life of Benito Mussolini. */}
                </p>
              </div>
              <h2 className="mt-6 text-lg font-semibold">{lg?.details || "Details"}</h2>
              <div className="mt-3 space-y-3 text-sm leading-6 text-slate-700">
                <p style={{ whiteSpace: "pre-wrap" }}>{eventData?.detail}</p>
              </div>
            </Card>
          </div>
        </Container>

        {/* Sticky bottom banner */}
        <div className="fixed inset-x-0 bottom-0 z-[300] bg-[#d9d9d9] shadow-[0_-4px_16px_rgba(0,0,0,0.15)] ring-1 ring-[#b3b3b3]">
          <div className="mx-auto w-full max-w-7xl px-6">
            <div className="flex flex-col items-center gap-4 py-[20px] sm:flex-row sm:justify-between">
              <div className="flex items-center gap-8 w-full sm:w-auto">
                <Pill className="bg-[#d9ead3] text-slate-700">BOOK CLUB</Pill>
                <div className="text-sm text-slate-700 font-medium">
                  {typeof spotsLeft === "number" && typeof totalSpots === "object" && totalSpots?.max
                    ? `${totalSpots.max - spotsLeft}/${totalSpots.max} ${lg?.joined || "joined"} · `
                    : ""}
                  {spotsLeft} {lg?.spotleft || "spots left"}
                </div>
                <div className="hidden w-80 sm:block">
                  {typeof spotsLeft === "number" && typeof totalSpots === "object" && totalSpots?.max ? (
                  <Progress value={pct} />
                  ) : null}
                </div>
              </div>
              <div className="flex w-full justify-end gap-3 sm:w-auto">
                <Button
                  variant={isJoined ? "secondary" : "primary"}
                  onClick={(e) => {
                    if (!user?._id) {
                      setShowLoginM(true);
                      return;
                    } else {
                      handleShowW(e);
                    }
                  }}
                  disabled={(typeof spotsLeft === "number" && spotsLeft === 0) || isJoined}
                  aria-pressed={isJoined}
                  data-testid="join-button"
                  className="min-w-[100px]"
                >
                  {typeof spotsLeft === "number" && spotsLeft === 0 ? (
                    <>{lg?.full || "Full"}</>
                  ) : isJoined ? (
                    <>
                      <Check size={16} /> {lg?.joinedCapitalized || "Joined"}
                    </>
                  ) : (
                    lg?.join || "Join"
                  )}
                </Button>
                <Button
                  disabled={typeof spotsLeft === "number" && spotsLeft === 0}
                  variant="secondary"
                  onClick={() => setShowShare(true)}
                  className="min-w-[100px]"
                >
                  <Share2 size={16} /> {lg?.share || "Share"}
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Share modal */}
        {showShare && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div
              className="absolute inset-0 bg-black/40"
              onClick={() => setShowShare(false)}
            />
            <div className="relative z-10 w-full max-w-lg rounded-2xl border-2 border-[#34c3c9] bg-white p-6 shadow-xl mx-4">
              <div className="mb-3 flex items-start justify-between">
                <h3 className="text-xl font-semibold text-[#0f172a]">
                  {lg?.shareThisEvent || "Share this event"}
                </h3>
                <button
                  className="text-slate-500"
                  onClick={() => setShowShare(false)}
                  aria-label="Close share"
                >
                  ✕
                </button>
              </div>

              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                {/* WhatsApp */}
                <a
                  href={`https://api.whatsapp.com/send?text=${encodedTitle}%20${encodedUrl}`}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-2 rounded-xl border px-3 py-2 text-sm hover:bg-[#f9f9f9]"
                >
                  <MessageCircle className="h-4 w-4" /> WhatsApp
                </a>

                {/* Facebook */}
                <a
                  href={`https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-2 rounded-xl border px-3 py-2 text-sm hover:bg-[#f9f9f9]"
                >
                  <Facebook className="h-4 w-4" /> Facebook
                </a>

                {/* Telegram */}
                <a
                  href={`https://t.me/share/url?url=${encodedUrl}&text=${encodedTitle}`}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-2 rounded-xl border px-3 py-2 text-sm hover:bg-[#f9f9f9]"
                >
                  <Send className="h-4 w-4" /> Telegram
                </a>

                {/* Instagram (no direct share API — open event link) */}
                <a
                  href={shareUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-2 rounded-xl border px-3 py-2 text-sm hover:bg-[#f9f9f9]"
                >
                  <Instagram className="h-4 w-4" /> Instagram
                </a>

                {/* TikTok (no web API — fallback to share link) */}
                <a
                  href={shareUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-2 rounded-xl border px-3 py-2 text-sm hover:bg-[#f9f9f9]"
                >
                  <Music className="h-4 w-4" /> TikTok
                </a>

                {/* Twitter/X */}
                <a
                  href={`https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-2 rounded-xl border px-3 py-2 text-sm hover:bg-[#f9f9f9]"
                >
                  <Twitter className="h-4 w-4" /> X / Twitter
                </a>

                {/* LinkedIn */}
                <a
                  href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-2 rounded-xl border px-3 py-2 text-sm hover:bg-[#f9f9f9]"
                >
                  <Linkedin className="h-4 w-4" /> LinkedIn
                </a>

                {/* Snapchat (limited support, fallback open with link) */}
                <a
                  href={`https://www.snapchat.com/scan?attachmentUrl=${encodedUrl}`}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-2 rounded-xl border px-3 py-2 text-sm hover:bg-[#f9f9f9]"
                >
                  <Camera className="h-4 w-4" /> Snapchat
                </a>

                {/* Copy Link */}
                <button
                  onClick={() =>
                    navigator.clipboard.writeText(shareUrl).then(() => {
                      toast.success("Link copied to clipboard!");
                    })
                  }
                  className="flex items-center gap-2 rounded-xl border px-3 py-2 text-sm hover:bg-[#f9f9f9]"
                >
                  <LinkIcon className="h-4 w-4" /> Copy link
                </button>

                {/* Email */}
                <a
                  href={`mailto:?subject=${encodedTitle}&body=${encodedUrl}`}
                  className="flex items-center gap-2 rounded-xl border px-3 py-2 text-sm hover:bg-[#f9f9f9]"
                >
                  <Mail className="h-4 w-4" /> Email
                </a>
              </div>
            </div>
          </div>
        )}

        {/* Joined confirmation modal */}
        {showJoined && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div
              className="absolute inset-0 bg-black/40"
              onClick={() => setShowJoined(false)}
            />
            <div className="relative mx-3 z-10 w-full max-w-2xl rounded-2xl border-2 border-[#34c3c9] bg-white p-6 shadow-xl">
              <div className="mb-3 flex items-start justify-between">
                <h3 className="text-xl font-semibold text-[#0f172a]">
                  {lg?.welldone}
                </h3>
                <button
                  className="text-slate-500"
                  onClick={() => {
                    setShowJoined(false);
                    if (user?.isAmbassador) {
                      navigate("/leading"); // Navigate to home page
                    } else {
                      navigate("/leading");
                    }
                  }}
                  aria-label="Close joined"
                >
                  ✕
                </button>
              </div>
              <p className="text-center text-sm sm:text-base text-slate-800 font-medium mb-2">
                {lg?.attending}{" "}
                <span className="font-semibold">
                  {" "}
                  {eventData?.title ? eventData?.title + " - " : " "}
                  Book Club!
                </span>
                !
              </p>
              <p className="text-center text-sm text-slate-600">
                {lg?.attendmsg}
              </p>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default GroupEvent;
